import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface RoundRobinParticipant {
  position: number;
  name: string;
  partner: string;
  wins: number;
  losses: number;
  setsFor: number;
  setsAgainst: number;
  gamesFor: number;
  gamesAgainst: number;
  difference: number;
  isCurrentUser: boolean;
}

interface LeagueMatchResult {
  team1_player1_name: string;
  team1_player2_name: string | null;
  team2_player1_name: string;
  team2_player2_name: string | null;
  team1_games_won: number | null;
  team1_games_lost: number | null;
  team2_games_won: number | null;
  team2_games_lost: number | null;
  winner_team: number | null;
}

export function useRoundRobinParticipants(eventId: string) {
  const [participants, setParticipants] = useState<RoundRobinParticipant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    const fetchParticipants = async () => {
      if (!eventId) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // Fetch confirmed registrations with player names
        const { data: registrations, error: regError } = await supabase
          .from('registrations')
          .select('id, user_id, partner_user_id, player1_name, player2_name')
          .eq('event_id', eventId)
          .eq('status', 'confirmed');

        if (regError) throw regError;

        // Resolve partner names from profiles when player2_name is missing
        const enrichedRegistrations = await Promise.all((registrations || []).map(async (reg) => {
          if (!reg.player2_name && reg.partner_user_id) {
            const { data: profile } = await supabase
              .from('profiles')
              .select('full_name')
              .eq('user_id', reg.partner_user_id)
              .single();
            return { ...reg, player2_name: profile?.full_name || null };
          }
          // Also resolve player1_name from profiles if missing
          if (!reg.player1_name && reg.user_id) {
            const { data: profile } = await supabase
              .from('profiles')
              .select('full_name')
              .eq('user_id', reg.user_id)
              .single();
            return { ...reg, player1_name: profile?.full_name || null };
          }
          return reg;
        }));

        // Fetch match results to calculate stats
        const { data: matchResults, error: matchError } = await supabase
          .from('league_match_results')
          .select('team1_player1_name, team1_player2_name, team2_player1_name, team2_player2_name, team1_games_won, team1_games_lost, team2_games_won, team2_games_lost, winner_team, sets')
          .eq('event_id', eventId);

        if (matchError) throw matchError;

        // Calculate stats for each registered pair
        const participantsData: RoundRobinParticipant[] = (enrichedRegistrations || []).map(reg => {
          const pairKey = `${reg.player1_name || ''}|${reg.player2_name || ''}`;
          let wins = 0;
          let losses = 0;
          let gamesFor = 0;
          let gamesAgainst = 0;
          let setsFor = 0;
          let setsAgainst = 0;

          // Iterate through all match results to calculate stats for this pair
          (matchResults || []).forEach((match: any) => {
            const team1Key = `${match.team1_player1_name || ''}|${match.team1_player2_name || ''}`;
            const team2Key = `${match.team2_player1_name || ''}|${match.team2_player2_name || ''}`;

            if (pairKey === team1Key) {
              gamesFor += match.team1_games_won || 0;
              gamesAgainst += match.team1_games_lost || 0;
              if (match.winner_team === 1) wins++;
              else if (match.winner_team === 2) losses++;
              // Count sets
              const matchSets = Array.isArray(match.sets) ? match.sets : [];
              matchSets.forEach((s: any) => {
                if (s.team1 > s.team2) setsFor++;
                else if (s.team2 > s.team1) setsAgainst++;
              });
            } else if (pairKey === team2Key) {
              gamesFor += match.team2_games_won || 0;
              gamesAgainst += match.team2_games_lost || 0;
              if (match.winner_team === 2) wins++;
              else if (match.winner_team === 1) losses++;
              const matchSets = Array.isArray(match.sets) ? match.sets : [];
              matchSets.forEach((s: any) => {
                if (s.team2 > s.team1) setsFor++;
                else if (s.team1 > s.team2) setsAgainst++;
              });
            }
          });

          return {
            position: 0,
            name: reg.player1_name || 'Jugador',
            partner: reg.player2_name || '',
            wins,
            losses,
            setsFor,
            setsAgainst,
            gamesFor,
            gamesAgainst,
            difference: gamesFor - gamesAgainst,
            isCurrentUser: reg.user_id === user?.id || reg.partner_user_id === user?.id
          };
        });

        // Sort: Points > Wins > Set difference > Game difference
        participantsData.sort((a, b) => {
          const pointsA = a.wins * 3;
          const pointsB = b.wins * 3;
          if (pointsB !== pointsA) return pointsB - pointsA;
          if (b.wins !== a.wins) return b.wins - a.wins;
          const setDiffA = a.setsFor - a.setsAgainst;
          const setDiffB = b.setsFor - b.setsAgainst;
          if (setDiffB !== setDiffA) return setDiffB - setDiffA;
          return b.difference - a.difference;
        });

        // Assign positions
        participantsData.forEach((p, index) => {
          p.position = index + 1;
        });

        setParticipants(participantsData);
      } catch (err: any) {
        console.error('Error fetching round robin participants:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchParticipants();

    // Real-time subscription for updates
    const channel = supabase
      .channel(`round-robin-participants-${eventId}`)
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'registrations', filter: `event_id=eq.${eventId}` }, 
        () => fetchParticipants()
      )
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'league_match_results', filter: `event_id=eq.${eventId}` }, 
        () => fetchParticipants()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [eventId, user?.id]);

  return { participants, isLoading, error };
}
